import { useEffect, useState, useCallback, useRef } from "react";
import { GraduationCap, FileText, Upload, Download, Trash2, Plus } from "lucide-react";
import { api, useAuth, formatApiError, downloadFile } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import StatutBadge from "./StatutBadge";
import { toast } from "sonner";

export default function EmployeeDetail({ employee, open, onOpenChange, onSaved }) {
  const { user } = useAuth();
  const readOnly = user?.role === "lecteur";
  const [historique, setHistorique] = useState([]);
  const [certificats, setCertificats] = useState([]);
  const [hDate, setHDate] = useState("");
  const [hType, setHType] = useState("MAC (7h)");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const loadCertificats = useCallback(() => {
    if (!employee) return;
    api.get(`/employees/${employee.id}/certificats`)
      .then((r) => setCertificats(r.data.certificats))
      .catch((e) => toast.error(formatApiError(e)));
  }, [employee]);

  useEffect(() => {
    if (open && employee) {
      setHistorique(employee.historique || []);
      setHDate("");
      loadCertificats();
    }
  }, [open, employee, loadCertificats]);

  if (!employee) return null;

  const addHistorique = async () => {
    if (!hDate) {
      toast.error("Choisissez une date");
      return;
    }
    try {
      const { data } = await api.post(`/employees/${employee.id}/historique`, { date: hDate, type: hType });
      setHistorique((prev) => [data, ...prev].sort((a, b) => (b.date || "").localeCompare(a.date || "")));
      setHDate("");
      toast.success("Formation ajoutée à l'historique");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const removeHistorique = async (h) => {
    try {
      await api.delete(`/employees/${employee.id}/historique/${h.hid}`);
      setHistorique((prev) => prev.filter((x) => x.hid !== h.hid));
      toast.success("Entrée supprimée");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/employees/${employee.id}/certificats`, form);
      toast.success("Certificat PDF téléversé");
      loadCertificats();
    } catch (err) {
      toast.error(formatApiError(err, "Échec du téléversement"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = (c) => downloadFile(`/certificats/${c.id}/download`, c.filename).catch((e) => toast.error(formatApiError(e, "Téléchargement impossible")));

  const removeCertificat = async (c) => {
    if (!window.confirm(`Supprimer le certificat "${c.filename}" ?`)) return;
    try {
      await api.delete(`/certificats/${c.id}`);
      toast.success("Certificat supprimé");
      loadCertificats();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="employee-detail-dialog">
        <DialogHeader>
          <DialogTitle>{employee.prenom} {employee.nom}</DialogTitle>
          <DialogDescription className="flex items-center gap-3 flex-wrap">
            <span>{employee.service}</span>
            <StatutBadge statut={employee.statut} joursRestants={employee.jours_restants} />
            {employee.fin_validite && <span className="font-mono text-xs">fin de validité : {employee.fin_validite}</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold mb-3">
              <GraduationCap className="h-4 w-4 text-accent" /> Historique des formations
            </h3>
            {!readOnly && (
              <div className="flex flex-wrap items-end gap-2 mb-3">
                <div className="space-y-1">
                  <Label className="text-xs">Date</Label>
                  <Input type="date" value={hDate} onChange={(e) => setHDate(e.target.value)} className="bg-background w-40" data-testid="historique-date-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Type</Label>
                  <Input value={hType} onChange={(e) => setHType(e.target.value)} className="bg-background w-44" data-testid="historique-type-input" />
                </div>
                <Button size="sm" onClick={addHistorique} data-testid="historique-add-button"><Plus className="h-4 w-4 mr-1" /> Ajouter</Button>
              </div>
            )}
            <div className="max-h-40 overflow-y-auto rounded-lg border border-border divide-y divide-border/50" data-testid="historique-list">
              {historique.length === 0 && <p className="px-3 py-4 text-sm text-muted-foreground text-center">Aucune formation enregistrée.</p>}
              {historique.map((h, i) => (
                <div key={h.hid || i} className="flex items-center justify-between px-3 py-2" data-testid={`historique-entry-${i}`}>
                  <span className="text-sm"><span className="font-mono text-xs mr-2">{h.date}</span>{h.type}
                    <span className="ml-2 text-xs text-muted-foreground">({h.source === "session" ? "session MAC" : h.source === "import" ? "import Excel" : h.source === "certificat" ? "certificat PDF" : "manuel"})</span>
                  </span>
                  {!readOnly && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-400" onClick={() => removeHistorique(h)} data-testid={`historique-delete-${i}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="flex items-center gap-2 text-sm font-semibold mb-3">
              <FileText className="h-4 w-4 text-accent" /> Certificats SST (PDF)
            </h3>
            {!readOnly && (
              <div className="mb-3">
                <input ref={fileRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={upload} data-testid="certificat-file-input" />
                <Button size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="certificat-upload-button">
                  <Upload className="h-4 w-4 mr-1.5" /> {uploading ? "Téléversement..." : "Téléverser un certificat PDF"}
                </Button>
                <span className="ml-2 text-xs text-muted-foreground">PDF uniquement, 5 Mo max</span>
              </div>
            )}
            <div className="max-h-40 overflow-y-auto rounded-lg border border-border divide-y divide-border/50" data-testid="certificats-list">
              {certificats.length === 0 && <p className="px-3 py-4 text-sm text-muted-foreground text-center">Aucun certificat téléversé.</p>}
              {certificats.map((c) => (
                <div key={c.id} className="flex items-center justify-between px-3 py-2" data-testid={`certificat-item-${c.id}`}>
                  <span className="text-sm truncate min-w-0 flex-1">{c.filename}
                    <span className="ml-2 text-xs text-muted-foreground font-mono">{Math.round(c.size / 1024)} Ko · {c.created_at?.slice(0, 10)}</span>
                  </span>
                  <div className="flex items-center">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => download(c)} data-testid={`certificat-download-${c.id}`}>
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                    {!readOnly && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-400" onClick={() => removeCertificat(c)} data-testid={`certificat-delete-${c.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
